import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import BarreHaute from "../components/BarreHaute";
import AccueilV2 from "./AccueilV2";
import GroupeV2 from "./GroupeV2";
import ChangeManagementV2 from "./ChangeManagementV2";
import ConversationsV2 from "./ConversationsV2";
import SaisieTempsV2 from "./SaisieTempsV2";
import MesTicketsV2 from "./MesTicketsV2";
import BibliothequesV2 from "./BibliothequesV2";
import GestionEquipeV2 from "./GestionEquipeV2";
import FicheCollaborateurV2 from "./FicheCollaborateurV2";
import DashboardManagerV2 from "./DashboardManagerV2";
import GestionTachesV2 from "./GestionTachesV2";
import AccueilArrivantV2 from "./AccueilArrivantV2";
import FicheProjetV2 from "./FicheProjetV2";
import MesProjetsV2 from "./MesProjetsV2";
import MesEtapesV2 from "./MesEtapesV2";
import ProjetsPilotageV2 from "./ProjetsPilotageV2";
import GestionLiensV2 from "./GestionLiensV2";
import SuiviOperationsV2 from "./SuiviOperationsV2";
import { CreationAD, HistoriqueAD, GestionAD } from "./ComptesADV2";
import { LIBELLES_ROLES, estVisible, filArianePour, trouverModule } from "../modulesV2";

// ===================================================================
// The Crowd v2 — Coque
//
// Tout ce qui est servi sous /v2 passe par ici : conteneur de theme,
// barre haute, routes de la v2. Les pages actuelles ne sont pas
// concernees — App.js ne monte cette coque que sous /v2.
//
// Theme : l'attribut data-theme est pose sur le conteneur .v2, pas sur
// <html>, pour que les jetons v2 ne debordent pas sur les pages
// actuelles. Le choix est conserve dans le navigateur ; a defaut, le
// theme sombre s'applique (meme mecanisme que Hopin).
// ===================================================================

const THEME_STORAGE_KEY = "thecrowd-v2-theme";

function themeInitial() {
  try {
    const stocke = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stocke === "light" || stocke === "dark") return stocke;
  } catch {
    // stockage indisponible (navigation privee, politique d'entreprise) :
    // on retombe sur le theme par defaut, sans casser l'application.
  }
  return "dark";
}

function CoqueV2({ collaborateur, nomUtilisateur, onLogout, onOuvrirModule }) {
  const [theme, setTheme] = useState(themeInitial);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // cf. themeInitial : l'absence de stockage ne doit rien empecher
    }
  }, [theme]);

  const role = collaborateur?.role;

  // Un module branche sur la v2 (routeV2) s'ouvre dans la coque ; les
  // autres passent encore par App.js et l'interface actuelle.
  const ouvrirModule = (module) => {
    if (module.routeV2) {
      navigate(module.routeV2);
      return;
    }
    onOuvrirModule(module);
  };

  // Garde d'affichage d'une route de module : hors profil, retour a
  // l'accueil. Commodite d'affichage seulement — le backend reste juge.
  const siVisible = (moduleId, element) =>
    estVisible(trouverModule(moduleId), collaborateur) ? element : <Navigate to="/v2" replace />;

  return (
    <div className="v2" data-theme={theme}>
      <BarreHaute
        filAriane={filArianePour(location.pathname)}
        nomUtilisateur={nomUtilisateur}
        libelleRole={LIBELLES_ROLES[role] || "Collaborateur"}
        theme={theme}
        onThemeChange={setTheme}
        onLogout={onLogout}
      />

      <main className="v2-contenu">
        <Routes>
          <Route
            path="/v2"
            element={<AccueilV2 collaborateur={collaborateur} nomUtilisateur={nomUtilisateur} onOuvrirModule={ouvrirModule} />}
          />
          <Route
            path="/v2/change-management"
            element={siVisible("change-management", <ChangeManagementV2 currentUser={collaborateur} />)}
          />
          <Route
            path="/v2/conversations"
            element={siVisible("conversations", <ConversationsV2 collaborateur={collaborateur} />)}
          />
          <Route
            path="/v2/activite/saisie"
            element={siVisible("saisie", <SaisieTempsV2 collaborateur={collaborateur} />)}
          />
          <Route
            path="/v2/activite/projets"
            element={siVisible("mes-projets", <MesProjetsV2 collaborateur={collaborateur} />)}
          />
          <Route
            path="/v2/activite/etapes"
            element={siVisible("mes-etapes", <MesEtapesV2 collaborateur={collaborateur} />)}
          />
          <Route
            path="/v2/activite/tickets"
            element={siVisible("mes-tickets", <MesTicketsV2 collaborateur={collaborateur} />)}
          />
          <Route
            path="/v2/bibliotheques"
            element={siVisible("bibliotheques", <BibliothequesV2 collaborateur={collaborateur} />)}
          />
          <Route
            path="/v2/pilotage/equipe"
            element={siVisible("equipe", <GestionEquipeV2 collaborateur={collaborateur} />)}
          />
          <Route
            path="/v2/pilotage/equipe/:id"
            element={siVisible("equipe", <FicheCollaborateurV2 />)}
          />
          <Route
            path="/v2/pilotage/dashboard"
            element={siVisible("dashboard", <DashboardManagerV2 collaborateur={collaborateur} />)}
          />
          <Route
            path="/v2/pilotage/projets"
            element={siVisible("projets", <ProjetsPilotageV2 collaborateur={collaborateur} />)}
          />
          <Route
            path="/v2/pilotage/liens"
            element={siVisible("liens", <GestionLiensV2 collaborateur={collaborateur} />)}
          />
          <Route
            path="/v2/pilotage/taches"
            element={siVisible("taches", <GestionTachesV2 collaborateur={collaborateur} />)}
          />
          <Route
            path="/v2/utilisateurs/accueil"
            element={siVisible("creation-compte", <AccueilArrivantV2 collaborateur={collaborateur} />)}
          />
          <Route
            path="/v2/utilisateurs/suivi"
            element={siVisible("suivi-operations", <SuiviOperationsV2 collaborateur={collaborateur} />)}
          />
          <Route
            path="/v2/utilisateurs/comptes-ad"
            element={siVisible("comptes-ad", <GroupeV2 collaborateur={collaborateur} onOuvrirModule={ouvrirModule} groupe={trouverModule("comptes-ad")} />)}
          />
          <Route
            path="/v2/utilisateurs/comptes-ad/creation"
            element={siVisible("ad-creation", <CreationAD collaborateur={collaborateur} />)}
          />
          <Route
            path="/v2/utilisateurs/comptes-ad/historique"
            element={siVisible("ad-historique", <HistoriqueAD collaborateur={collaborateur} />)}
          />
          <Route
            path="/v2/utilisateurs/comptes-ad/gestion"
            element={siVisible("ad-gestion", <GestionAD collaborateur={collaborateur} />)}
          />
          {/* Refonte Projets : fiche ouverte depuis Mes projets, Mes etapes et la
              vue manager. Pas de garde de profil : toute personne ayant un role
              sur le projet y accede, le backend controle (mes_droits). */}
          <Route
            path="/v2/projets/:id"
            element={<FicheProjetV2 collaborateur={collaborateur} />}
          />
          <Route
            path="/v2/:groupeId"
            element={<GroupeV2 collaborateur={collaborateur} onOuvrirModule={ouvrirModule} />}
          />
          <Route path="*" element={<Navigate to="/v2" />} />
        </Routes>
      </main>
    </div>
  );
}

export default CoqueV2;
